import {
  Checkbox,
  FormControl,
  FormErrorMessage,
  HStack,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  FormRow,
  InputDatePicker,
  Select,
  TimeInput,
} from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import {
  useTimeTrackingOptionsForProjectQuery,
  useTimeTrackingProjectsQuery,
} from "@src/__generated__/graphql";
import { BillableButton } from "@src/components/ui-kit/BillableButton";
import { useSetEditorContent } from "@src/components/ui-kit/TextEditor/hooks/useSetContent";
import {
  TextEditor,
  type TextEditorRef,
} from "@src/components/ui-kit/TextEditor/TextEditor";
import { onError } from "@src/utils/apollo";
import { cannot } from "@src/utils/components/permissions";
import { toApiDate } from "@src/utils/dates";
import {
  fieldToInputProps,
  fieldToSelectProps,
} from "@src/utils/forms/inputHelpers";
import { useStore } from "@src/utils/hooks";
import map from "@src/utils/map-to-options";
import {
  type TaskOption,
  TaskSelect,
} from "@src/widgets/TaskSelect/TaskSelect";
import { observer } from "mobx-react-lite";
import { now } from "mobx-utils";
import React, { useEffect, useMemo, useRef } from "react";
import type { EntryForm } from "../form";

interface FormProps {
  value: EntryForm;
  isReadOnly?: boolean;
}

const Form = ({ value: form, isReadOnly = false }: FormProps) => {
  const { authStore } = useStore();
  const editorRef = useRef<TextEditorRef>(null);

  const projectsReq = useTimeTrackingProjectsQuery({
    ...onError(),
  });

  const projectsOptions = useMemo(() => {
    if (!projectsReq.data?.projectsSimpleMap) return [];
    return map.projects(projectsReq.data.projectsSimpleMap);
  }, [projectsReq.data]);

  const projectReqDate = useMemo(() => {
    return toApiDate(form.tracked_for_date.value ?? new Date(now()));
  }, [form.tracked_for_date.value]);

  const forProjectReq = useTimeTrackingOptionsForProjectQuery({
    skip: !form.project_id.value,
    variables: {
      id: form.project_id.value,
      date: projectReqDate,
    },
    ...onError(),
  });

  const [ourWorkOptions, workTypeOptions] = useMemo(() => {
    const forProject = forProjectReq.data?.timeTrackingOptionsForProject;
    if (!forProject) return [[], []];

    const ourWorkOptions = forProject.ourWork.flatMap((i) => {
      if (!i.timeTrackingSettings.tracking_enabled) return [];

      return [{ label: `${i.title} - ${i.category.name}`, value: i.id }];
    });
    const workTypeOptions = map.toOptions(
      forProject.availableTimeTrackingWorkTypes,
      "title",
    );
    return [ourWorkOptions, workTypeOptions];
  }, [forProjectReq.data]);

  // preset ourWork
  useEffect(() => {
    if (!form.budget_item_id.value && ourWorkOptions.length === 1) {
      form.budget_item_id.onChange(ourWorkOptions[0].value);
    }
  }, [form.budget_item_id.value, ourWorkOptions]);

  // preset workType
  useEffect(() => {
    if (
      !form.time_tracking_work_type_id.value &&
      workTypeOptions.length === 1
    ) {
      form.time_tracking_work_type_id.onChange(workTypeOptions[0].value);
    }
  }, [form.time_tracking_work_type_id.value, workTypeOptions]);

  // preset workType by task
  useEffect(() => {
    if (
      form.time_tracking_work_type_id.value ||
      !form.task_id.value ||
      workTypeOptions.length === 0
    )
      return;

    const myTaskPositions =
      forProjectReq.data?.timeTrackingOptionsForProject.ourWork
        .flatMap((i) => i.tasks)
        .find((i) => i.id === form.task_id.value)
        ?.positions.filter((i) => i.user?.id === authStore.user?.id);
    if (myTaskPositions && myTaskPositions.length === 1) {
      form.time_tracking_work_type_id.onChange(
        myTaskPositions[0].timeTrackingWorkType.id,
      );
      return;
    }

    if (
      workTypeOptions.find(
        (i) => i.value === authStore.user?.profile?.defaultWorktype.id,
      )
    ) {
      form.time_tracking_work_type_id.onChange(
        authStore.user!.profile!.defaultWorktype.id,
      );
    }
  }, [form.task_id.value, forProjectReq.data?.timeTrackingOptionsForProject]);

  useSetEditorContent({
    ref: editorRef?.current,
    note: form.notes.value,
  });

  const onSelectProject = (val: string) => {
    form.project_id.onChange(val);
    resetOptionsForProject();
  };

  const onSelectBudgetItem = (val: string | undefined) => {
    form.budget_item_id.onChange(val ?? "");
    form.task_id.reset();
  };

  const resetOptionsForProject = () => {
    form.budget_item_id.reset();
    form.task_id.reset();
    form.time_tracking_work_type_id.reset();
  };

  const onTaskSelectChange = (
    ...[id, { budgetItem, project, label, billable }]: [
      value: TaskOption["value"],
      data: Partial<TaskOption>,
    ]
  ) => {
    form.task_id.onChange(id);
    if (label) {
      form.selectedTask = { value: id, label };
    }
    budgetItem && form.budget_item_id.onChange(budgetItem.id);
    project && form.project_id.onChange(project.id);
    form.billable.onChange(billable ?? false);
  };

  return (
    <React.Fragment>
      <FormRow title={t`Task`}>
        <TaskSelect
          disableDoneTasks
          hideNonTrackableTasks
          placeholder={t`Select task`}
          onChange={(id, data) =>
            onTaskSelectChange(id as string, data as Partial<TaskOption>)
          }
          value={form.task_id.value}
          projectFilter={
            form.project_id.value ? [form.project_id.value] : undefined
          }
          budgetItemFilter={
            form.budget_item_id.value ? [form.budget_item_id.value] : undefined
          }
          error={form.task_id.error}
          defaultOptions={form.selectedTask ? [form.selectedTask] : undefined}
          options={[]}
          isDisabled={isReadOnly}
        />
      </FormRow>

      <FormRow title={t`Project`}>
        <Select
          isClearable
          placeholder={t`Select project`}
          isLoading={projectsReq.loading}
          value={form.project_id.value}
          error={form.project_id.error}
          options={projectsOptions}
          onChange={onSelectProject}
          isDisabled={isReadOnly}
        />
      </FormRow>

      <FormRow title={t`Budget item`}>
        <Select
          isClearable
          placeholder={t`Select budget item`}
          isLoading={forProjectReq.loading}
          value={form.budget_item_id.value}
          error={form.budget_item_id.error}
          onChange={onSelectBudgetItem}
          options={ourWorkOptions}
          isDisabled={isReadOnly}
        />
      </FormRow>

      <FormRow title={t`Position`}>
        <Select
          placeholder={t`Select position`}
          isLoading={forProjectReq.loading}
          {...fieldToSelectProps(
            form.time_tracking_work_type_id,
            workTypeOptions,
          )}
          isDisabled={isReadOnly}
        />
      </FormRow>

      <FormRow
        alignItems="start"
        title={
          <VStack align="start" spacing="2">
            <HStack spacing="2">
              {form.specificTimeEnabled.value ? (
                <HStack align="start" spacing="2">
                  <FormControl isInvalid={form.specific_time_from.hasError}>
                    <TimeInput
                      value={form.specific_time_from.value}
                      onChange={form.specific_time_from.onChange}
                      isDisabled={
                        isReadOnly || cannot("timeTracking_timer_use_manual")
                      }
                    />
                    <FormErrorMessage>
                      {form.specific_time_from.error}
                    </FormErrorMessage>
                  </FormControl>
                  <Text pt="5" lineHeight="0">
                    &nbsp;-&nbsp;
                  </Text>
                  <FormControl isInvalid={form.specific_time_to.hasError}>
                    <TimeInput
                      value={form.specific_time_to.value}
                      onChange={form.specific_time_to.onChange}
                      isDisabled={
                        isReadOnly || cannot("timeTracking_timer_use_manual")
                      }
                    />
                    <FormErrorMessage>
                      {form.specific_time_to.error}
                    </FormErrorMessage>
                  </FormControl>
                </HStack>
              ) : (
                <TimeInput
                  height="60px"
                  fontSize="3rem"
                  value={form.time.value}
                  onChange={form.time.onChange}
                  isDisabled={
                    isReadOnly || cannot("timeTracking_timer_use_manual")
                  }
                />
              )}
            </HStack>
            <HStack align="center" spacing="2">
              <BillableButton
                field={form.billable}
                size="lg"
                isDisabled={
                  isReadOnly || cannot("timeTracking_timer_use_manual")
                }
              />
              <Checkbox
                isChecked={form.specificTimeEnabled.value}
                isDisabled={
                  isReadOnly || cannot("timeTracking_timer_use_manual")
                }
                onChange={({ target }) => {
                  form.toggleSpecificTime(target.checked);
                }}
              >
                <Trans>Specific time</Trans>
              </Checkbox>
            </HStack>
          </VStack>
        }
      >
        <TextEditor
          ref={editorRef}
          isEditable
          attachments={[]}
          placeholder={t`Notes`}
          h="60px"
          overflow="hidden"
          maxH="60px"
          px="3"
          minHeight="60px"
          borderWidth="thin"
          initialValue={form.notes.value}
          borderColor="grey.200"
          borderRadius="base"
          transition="border-color 150ms ease-in"
          _focusWithin={{
            borderColor: "purple.500",
          }}
          {...fieldToInputProps(form.notes)}
        />
      </FormRow>
      {form.editMode && (
        <FormRow title={t`Date`}>
          <InputDatePicker
            onChange={(val) => {
              if (val) {
                form.tracked_for_date.onChange(val.start);
              }
            }}
            selected={form.tracked_for_date.value}
            isDisabled={isReadOnly}
          />
        </FormRow>
      )}
    </React.Fragment>
  );
};

export default observer(Form);
